import { API_ORIGIN, apiUrl } from "./apiOrigin";
// InspectorSummaryPublic.js
// =============================================================================
// Reached via /?insp=TOKEN (minted by "Email inspector summary" on the Single
// pharmacy page — see SinglePharmacy.js / inspector_summary_routes.js). No
// login, no nav rail — the token in the URL plus a 6-digit PIN (given to the
// inspector separately from the emailed link) are the only credential. The
// link expires 6 hours after it was created.
// =============================================================================

import React, { useState, useCallback } from "react";
const API_BASE = apiUrl("");
async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body || {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}
const S = {
  page: {
    minHeight: "100vh",
    background: "#F5F5F5",
    fontFamily: "system-ui, sans-serif",
    padding: "32px 16px"
  },
  gateWrap: {
    maxWidth: 380,
    margin: "80px auto",
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 10,
    padding: 28,
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)"
  },
  h1: {
    fontSize: 18,
    fontWeight: 700,
    margin: "0 0 6px",
    color: "#262626"
  },
  sub: {
    fontSize: 13,
    color: "#5D5D5D",
    margin: "0 0 18px",
    lineHeight: 1.5
  },
  pinInput: {
    width: "100%",
    fontSize: 24,
    letterSpacing: 6,
    textAlign: "center",
    padding: "10px 8px",
    border: "1px solid #D9D9D9",
    borderRadius: 8,
    marginBottom: 12,
    boxSizing: "border-box"
  },
  btn: {
    width: "100%",
    background: "#4f46e5",
    color: "#fff",
    border: 0,
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer"
  },
  err: {
    fontSize: 13,
    color: "#A8071A",
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 6,
    padding: "8px 12px",
    marginTop: 12
  },
  wrap: {
    maxWidth: 760,
    margin: "0 auto"
  },
  headerCard: {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 10,
    padding: "18px 22px",
    marginBottom: 16
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#262626",
    margin: 0
  },
  meta: {
    fontSize: 13,
    color: "#5D5D5D",
    marginTop: 6,
    lineHeight: 1.6
  },
  section: {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    marginBottom: 10,
    overflow: "hidden"
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    cursor: "pointer"
  },
  sectionLabel: {
    fontSize: 14.5,
    fontWeight: 700,
    color: "#262626"
  },
  sectionCount: {
    fontSize: 13,
    color: "#434343"
  },
  sectionNote: {
    fontSize: 12,
    color: "#8C8C8C",
    marginTop: 2
  },
  chevron: {
    fontSize: 12,
    color: "#8C8C8C",
    marginLeft: 10
  },
  detailWrap: {
    borderTop: "1px solid #F5F5F5",
    padding: "10px 16px 14px",
    maxHeight: 420,
    overflowY: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12.5
  },
  th: {
    textAlign: "left",
    padding: "6px 8px",
    color: "#5D5D5D",
    fontWeight: 600,
    borderBottom: "1px solid #F0F0F0",
    whiteSpace: "nowrap"
  },
  td: {
    padding: "6px 8px",
    borderBottom: "1px solid #F5F5F5",
    color: "#262626",
    verticalAlign: "top"
  }
};
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? "—" : dt.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}
function fmtDay(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? "—" : dt.toLocaleDateString("en-GB", {
    dateStyle: "medium"
  });
}

// Column layout per section — keeps the detail table generic instead of one
// bespoke component per heading.
const SECTION_COLUMNS = {
  cleaning: [["date", "Date", fmtDate], ["area", "Area"], ["notes", "Notes"]],
  fridge: [["date", "Date", fmtDate], ["fridge", "Fridge"], ["minTemperature", "Min °C"], ["maxTemperature", "Max °C"], ["comments", "Comments"]],
  dateChecking: [["date", "Date", fmtDate], ["area", "Area"]],
  sop: [["date", "Signed", fmtDate], ["sop", "SOP"], ["staff", "Staff"]],
  feedback: [["date", "Date", fmtDate], ["rating", "Rating"], ["isComplaint", "Complaint?", v => v ? "Yes" : "No"], ["reviewed", "Reviewed?", v => v ? "Yes" : "No"], ["comments", "Comments"]],
  rpSignIn: [["signInTime", "Signed in", fmtDate], ["signOutTime", "Signed out", fmtDate], ["pharmacist", "Pharmacist"], ["cdKeysReturnedToSafe", "CD keys returned", v => v == null ? "—" : v ? "Yes" : "No"]],
  visitorLog: [["date", "Date", fmtDate], ["name", "Visitor"], ["organisation", "Organisation"], ["purpose", "Purpose"], ["signOutAt", "Signed out", fmtDate]],
  accidentBook: [["date", "Date", fmtDate], ["location", "Location"], ["reportedBy", "Reported by"], ["patientName", "Patient"], ["reviewed", "Reviewed?", v => v ? "Yes" : "No"]],
  nearMiss: [["date", "Date", fmtDate], ["category", "Category"], ["description", "Description"]],
  nearMissSummaries: [["month", "Month"], ["totalIncidents", "Incidents"], ["rate", "Rate"], ["narrative", "Narrative"]],
  medicationIncidents: [["date", "Date", fmtDate], ["harmLevelId", "Harm level"], ["submittedToLfpse", "Submitted to LFPSE?", v => v ? "Yes" : "No"], ["rpNotified", "RP notified?", v => v ? "Yes" : "No"]],
  mhra: [["date", "Date", fmtDate], ["title", "Alert"], ["classLevel", "Class"], ["isAffected", "Affected?", v => v ? "Yes" : "No"], ["actionTakenByStaff", "Action taken"]]
};
function SectionDetail({
  rows,
  section
}) {
  const cols = SECTION_COLUMNS[section] || [];
  if (!rows.length) return <div style={{
    fontSize: 13,
    color: "#5D5D5D",
    padding: "4px 2px"
  }}>No entries in this period.</div>;
  return <table style={S.table}>
      <thead><tr>{cols.map(([key, label]) => <th key={key} style={S.th}>{label}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => <tr key={i}>
            {cols.map(([key,, fmt]) => <td key={key} style={S.td}>{fmt ? fmt(r[key]) : r[key] ?? "—"}</td>)}
          </tr>)}
      </tbody>
    </table>;
}
function SectionRow({
  section,
  token,
  pin
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (rows !== null) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiPost("/api/inspector-summary/section", {
        token,
        pin,
        section: section.key
      });
      setRows(r.rows);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };
  return <div style={S.section}>
      <div style={S.sectionHead} onClick={toggle}>
        <div>
          <div style={S.sectionLabel}>{section.label}</div>
          <div style={S.sectionNote}>{section.note}</div>
        </div>
        <div style={{
        display: "flex",
        alignItems: "center"
      }}>
          <span style={S.sectionCount}>{section.count}</span>
          <span style={S.chevron}>{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && <div style={S.detailWrap}>
          {loading && <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>Loading…</div>}
          {err && <div style={S.err}>{err}</div>}
          {rows && <SectionDetail rows={rows} section={section.key} />}
        </div>}
    </div>;
}
function PinGate({
  token,
  onUnlocked
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async e => {
    e.preventDefault();
    if (pin.length !== 6) {
      setErr("Enter the 6-digit PIN given to you by the pharmacy.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await apiPost("/api/inspector-summary/verify", {
        token,
        pin
      });
      onUnlocked(pin, r);
    } catch (e2) {
      setErr(e2.message);
    }
    setBusy(false);
  };
  return <div style={S.gateWrap}>
      <div style={S.h1}>Compliance summary</div>
      <div style={S.sub}>
        Enter the 6-digit PIN the pharmacy gave you to view this pharmacy's compliance activity summary.
      </div>
      <form onSubmit={submit}>
        <input style={S.pinInput} inputMode="numeric" maxLength={6} placeholder="000000" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus />
        <button type="submit" style={S.btn} disabled={busy}>{busy ? "Checking…" : "View summary"}</button>
      </form>
      {err && <div style={S.err}>{err}</div>}
    </div>;
}
function SummaryView({
  token,
  pin
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiPost("/api/inspector-summary/summary", {
        token,
        pin
      });
      setData(r);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [token, pin]);
  React.useEffect(() => {
    load();
  }, [load]);
  if (loading) return <div style={S.wrap}><div style={{
      color: "#5D5D5D",
      fontSize: 13
    }}>Loading…</div></div>;
  if (err) return <div style={S.wrap}><div style={S.err}>{err}</div></div>;
  if (!data) return null;
  return <div style={S.wrap}>
      <div style={S.headerCard}>
        <div style={S.title}>{data.pharmacyName} — compliance summary</div>
        <div style={S.meta}>
          Generated {fmtDate(data.generatedAt)} · Covers {fmtDay(data.periodFrom)} to {fmtDay(data.periodTo)} (previous 12 months)
          <br />
          This link expires {fmtDate(data.expiresAt)}.
        </div>
      </div>
      {data.sections.map(s => <SectionRow key={s.key} section={s} token={token} pin={pin} />)}
    </div>;
}
export default function InspectorSummaryPublic({
  token
}) {
  const [pin, setPin] = useState(null);
  return <div style={S.page}>
      {!pin ? <PinGate token={token} onUnlocked={enteredPin => setPin(enteredPin)} /> : <SummaryView token={token} pin={pin} />}
    </div>;
}
